/**
 * <shade-selected-display> — shows the matching shade-guide item under the selected
 * shade in the main-product-v2 concealer-variant picker.
 *  - Buy 1 (swatch picker): updates the global display
 *  - Buy 3 (dropdown picker): each dropdown updates only its own display
 *
 * v2-scoped port of assets/shade-selected-display.js (<aiw-shade-selected-display>),
 * scoped strictly to .main-product-v2 and reading from a metaobject-driven
 * .shade-guide-source (snippets/shade-guide-v2.liquid). AIW file left untouched.
 */
if (customElements.get('shade-selected-display') === undefined) {
  class ShadeSelectedDisplay extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.labelEnterListeners = [];
      this.labelLeaveListeners = [];
      this.fadeDisplays = new Set();
    }

    connectedCallback() {
      // Scope strictly to this product section. Don't fall back to document — a
      // document-wide listener could rewrite another product's descriptor on a
      // multi-section page, and the failure would be invisible.
      this.root = this.closest('.main-product-v2');
      if (!this.root) {
        if (typeof console !== 'undefined') {
          console.warn('[shade-selected-display] no .main-product-v2 scope — display inert');
        }
        return;
      }

      this.source = this.root.querySelector('.shade-guide-source');
      if (!this.source) return;

      this.root.addEventListener('change', this.onChange);

      this.root.querySelectorAll('.concealer-variant__shade-label, .swatch-input__label').forEach((label) => {
        const radio = this.getRadioFromLabel(label);
        if (!radio || radio.type !== 'radio') return;

        const isBuy3 = radio.classList.contains('concealer-variant__shade-radio');

        const enter = () => {
          const display = isBuy3 ? this.findDisplay(radio) : this.root.querySelector('[data-shade-selected="global"]');
          this.renderShade(display, radio.dataset.shadeTitle || radio.value);
        };

        const leave = () => {
          if (isBuy3) {
            const container = radio.closest('.select.concealer-variant__shade');
            const display = container && container.querySelector('[data-shade-selected="local"]');
            this.renderShade(display, this.getCheckedTitle(container, '.concealer-variant__shade-radio'));
          } else {
            const globalDisplay = this.root.querySelector('[data-shade-selected="global"]');
            this.renderShade(globalDisplay, this.getCheckedTitle(this.root, '.swatch-input__input'));
          }
        };

        label.addEventListener('mouseenter', enter);
        label.addEventListener('mouseleave', leave);
        this.labelEnterListeners.push({ label, fn: enter });
        this.labelLeaveListeners.push({ label, fn: leave });
      });

      this.updateAll();
    }

    disconnectedCallback() {
      if (this.root) this.root.removeEventListener('change', this.onChange);
      this.labelEnterListeners.forEach(({ label, fn }) => label.removeEventListener('mouseenter', fn));
      this.labelLeaveListeners.forEach(({ label, fn }) => label.removeEventListener('mouseleave', fn));
      this.fadeDisplays.forEach((display) => {
        if (display._fadeTimer) {
          clearTimeout(display._fadeTimer);
          display._fadeTimer = null;
        }
        display._pendingHtml = null;
      });
      this.fadeDisplays.clear();
    }

    findDisplay(radio) {
      const localContainer = radio.closest('.select.concealer-variant__shade');
      if (localContainer) {
        return localContainer.querySelector('[data-shade-selected="local"]');
      }
      return this.root.querySelector('[data-shade-selected="global"]');
    }

    renderShade(display, title) {
      if (!display) return;

      let html = '';
      if (title) {
        const key = title.toLowerCase().trim();
        const match = this.source.querySelector('[data-shade-name="' + key + '"]');
        // Warn (rather than silently blank) so a title/guide-key mismatch is debuggable in QA.
        if (!match && typeof console !== 'undefined') {
          console.warn('[shade-selected-display] no shade-guide entry for "' + key + '"');
        }
        const text = match && match.querySelector('.shade-guide__text');
        if (text) html = text.outerHTML;
      }

      // Compare against the PENDING value, not the rendered one. A mouse sweep faster
      // than the 45ms fade would otherwise short-circuit here (the rendered HTML still
      // matches) without cancelling the in-flight timer, which then writes the
      // hovered-over shade and leaves the descriptor contradicting the selected one.
      const current = display._fadeTimer ? display._pendingHtml : display.innerHTML;
      if (current === html) return;

      if (!display.style.transition) {
        display.style.transition = 'opacity 0.05s ease';
      }

      if (display._fadeTimer) clearTimeout(display._fadeTimer);
      display.style.opacity = '0';

      this.fadeDisplays.add(display);
      display._pendingHtml = html;
      display._fadeTimer = setTimeout(() => {
        display.innerHTML = html;
        display.style.opacity = '1';
        display._fadeTimer = null;
        display._pendingHtml = null;
        this.fadeDisplays.delete(display);
      }, 45);
    }

    updateAll() {
      this.root.querySelectorAll('.select.concealer-variant__shade').forEach((container) => {
        const checked = container.querySelector('.concealer-variant__shade-radio:checked');
        const display = container.querySelector('[data-shade-selected="local"]');
        this.renderShade(display, checked ? checked.dataset.shadeTitle || checked.value : '');
      });

      const buy1 = this.root.querySelector('.swatch-input__input:checked');
      const globalDisplay = this.root.querySelector('[data-shade-selected="global"]');
      this.renderShade(globalDisplay, buy1 ? buy1.dataset.shadeTitle || buy1.value : '');
    }

    onChange(e) {
      const t = e.target;
      if (!t || t.type !== 'radio') return;
      if (t.classList.contains('concealer-variant__shade-radio')) {
        const display = this.findDisplay(t);
        this.renderShade(display, t.dataset.shadeTitle || t.value);
      } else if (t.classList.contains('swatch-input__input')) {
        const globalDisplay = this.root.querySelector('[data-shade-selected="global"]');
        this.renderShade(globalDisplay, t.dataset.shadeTitle || t.value);
      }
    }

    getRadioFromLabel(label) {
      return label.querySelector('input[type="radio"]') || label.previousElementSibling;
    }

    getCheckedTitle(scope, selector) {
      const checked = scope.querySelector(selector + ':checked');
      return checked ? checked.dataset.shadeTitle || checked.value : '';
    }
  }

  customElements.define('shade-selected-display', ShadeSelectedDisplay);
}
